import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import moment from 'moment';
import { Moment } from 'moment';
import { DateModel, TIMEZONE_CONFIG } from '@platform500services/p500-ui-kit';
import { TimeZoneConfig } from '../../time-zone/time-zone.config';
import { ICurrentInterval, IIntervals, IRange } from './date-interval.model';

@Component({
  selector: 'gtd-date-interval',
  templateUrl: './date-interval.component.html',
})
export class DateIntervalComponent implements OnInit {
  constructor(@Inject(TIMEZONE_CONFIG) private timeZoneConfig: TimeZoneConfig) {}
  @Input() public ranges: Partial<Record<IIntervals, IRange>> = {
    'last-hour': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).subtract(1, 'hours'),
        to: moment().utcOffset(this.timeZoneConfig.gmt),
      }),
      title: $localize`:@@dates.last_hour:Last Hour`,
    },
    '6-hours': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).subtract(6, 'hours'),
        to: moment().utcOffset(this.timeZoneConfig.gmt),
      }),
      title: $localize`:@@dates.six_hours:6 Hours`,
    },
    '12-hours': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).subtract(12, 'hours'),
        to: moment().utcOffset(this.timeZoneConfig.gmt),
      }),
      title: $localize`:@@dates.twelve_hours:12 Hours`,
    },
    'this-month': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).startOf('month'),
        to: moment().utcOffset(this.timeZoneConfig.gmt).endOf('month'),
      }),
      title: $localize`:@@dates.this_month:This Month`,
    },
    yesterday: {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).subtract(1, 'day').startOf('day'),
        to: moment().utcOffset(this.timeZoneConfig.gmt).subtract(1, 'day').endOf('day'),
      }),
      title: $localize`:@@dates.yesterday:Yesterday`,
    },
    today: {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).startOf('day'),
        to: moment().utcOffset(this.timeZoneConfig.gmt).endOf('day'),
      }),
      title: $localize`:@@dates.today:Today`,
    },
    'week-to-date': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).startOf('isoWeek'),
        to: moment().utcOffset(this.timeZoneConfig.gmt).endOf('isoWeek'),
      }),
      title: $localize`:@@dates.this_week:This Week`,
    },
    'year-to-date': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).startOf('year'),
        to: moment().utcOffset(this.timeZoneConfig.gmt).endOf('year'),
      }),
      title: $localize`:@@dates.this_year:This Year`,
    },
    'last-month': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).subtract(1, 'month').startOf('month'),
        to: moment().utcOffset(this.timeZoneConfig.gmt).subtract(1, 'month').endOf('month'),
      }),
      title: $localize`:@@dates.last_month:Last Month`,
    },
    'last-week': {
      value: () => ({
        from: moment().utcOffset(this.timeZoneConfig.gmt).subtract(1, 'weeks').startOf('isoWeek'),
        to: moment().utcOffset(this.timeZoneConfig.gmt).subtract(1, 'weeks').endOf('isoWeek'),
      }),
      title: $localize`:@@dates.last_week:Last Week`,
    },
  };
  public shownIntervalsSet: Set<IIntervals> = new Set(['today', 'this-month', 'year-to-date']);

  @Output() intervalSelected = new EventEmitter<DateModel<Moment>>();
  @Output() currentIntervalChange = new EventEmitter<ICurrentInterval>();
  @Input() currentInterval: ICurrentInterval = 'this-month';
  @Input() intervals: IIntervals[] = ['today', 'yesterday', 'week-to-date', 'last-week', 'this-month', 'last-month', 'year-to-date'];
  @Input() alwaysHideIntervals: boolean = false;
  @Input() disabled = false;
  @Input() customClass!: string;
  @Input() selectLabel!: string;
  @Input() set shownIntervals(value: IIntervals[]) {
    this.shownIntervalsSet = new Set(value);
  }
  @Input() dateRangeDropdownPosition = 'right';

  ngOnInit(): void {
    setTimeout(() => {
      this.selectedInterval(this.currentInterval);
    }, 0);
  }

  selectedInterval(interval: ICurrentInterval) {
    if (interval) {
      this.intervalSelected.emit(this.ranges[interval].value());
      this.currentInterval = interval;
      this.currentIntervalChange.emit(this.currentInterval);
    }
  }
}
